import { accountStrings } from './accountStrings';
import type { Catalogue } from './types';

/**
 * The messages every client shares: sign-in, session, errors, and the vocabulary of the domain that
 * appears in more than one application. Each app merges its own catalogue over this one.
 *
 * Arabic is written first because it is the source language here, not a translation of the English.
 */
export const commonStrings: Catalogue = {
  ar: {
    // Accessibility
    'a11y.skipToContent': 'تخطَّ إلى المحتوى',
    'a11y.mainNavigation': 'التنقّل الرئيسي',
    'nav.menu': 'القائمة',

    // Product
    'app.daghfal': 'دغفل',
    'app.tagline': 'منصّة إدارة الحضانات ورياض الأطفال',

    // Sign-in
    'auth.signIn': 'تسجيل الدخول',
    'auth.signOut': 'تسجيل الخروج',
    'auth.phone': 'رقم الهاتف',
    'auth.phone.hint': 'أدخل رقمك بالصيغة الدولية، مثال ‎+963912345678',
    'auth.password': 'كلمة السر',
    'auth.password.show': 'إظهار كلمة السر',
    'auth.password.hide': 'إخفاء كلمة السر',
    'auth.submit': 'دخول',
    'auth.submitting': 'جارٍ الدخول…',
    'auth.phone.required': 'أدخل رقم الهاتف.',
    'auth.password.required': 'أدخل كلمة السر.',
    'auth.welcome': 'مرحباً {name} 🌷',

    // Membership selection (D-2)
    'auth.chooseMembership': 'اختر الحضانة',
    'auth.chooseMembership.hint': 'حسابك مرتبط بأكثر من جهة. اختر الجهة التي تريد الدخول إليها الآن.',
    'auth.membership.platform': 'إدارة المنصّة',
    'auth.membership.opensIn.guardian': 'يُفتح من تطبيق الأهل',
    'auth.membership.opensIn.staff': 'يُفتح من تطبيق المعلّمات',
    'auth.membership.opensIn.admin': 'يُفتح من لوحة إدارة الحضانة',
    'auth.membership.opensIn.platform': 'يُفتح من وحدة تحكّم المنصّة',
    'auth.membership.elsewhere.hint': 'هذا الحساب لا يُستخدم في هذا التطبيق. افتح التطبيق المذكور أعلاه، أو سجّل الدخول بحساب آخر.',
    'auth.signInAgain': 'تسجيل الدخول من جديد',
    'auth.switchMembership': 'تبديل الجهة',

    // Invitation
    'auth.invitation.title': 'قبول الدعوة',
    'auth.invitation.newPassword': 'كلمة السر الجديدة',
    'auth.invitation.confirmPassword': 'تأكيد كلمة السر',
    'auth.invitation.mismatch': 'كلمتا السر غير متطابقتين.',
    'auth.invitation.accept': 'انضمام',

    // Sessions
    'session.title': 'الأجهزة النشطة',
    'session.signOutAll': 'تسجيل الخروج من كل الأجهزة',
    'session.startedAt': 'بدأت في {date}',
    'session.expiresAt': 'تنتهي في {date}',

    // Errors and states
    'error.title': 'حدث خطأ',
    'error.network': 'تعذّر الاتصال بالخادم. تحقّق من اتصالك بالإنترنت ثم أعد المحاولة.',
    'error.unexpected': 'حدث خطأ غير متوقّع. أعد المحاولة، وإذا تكرّر تواصل مع الدعم.',
    'error.forbidden': 'لا تملك صلاحية الوصول إلى هذه الصفحة.',
    'error.notFound': 'الصفحة غير موجودة.',
    'error.reference': 'رقم المرجع: {reference}',
    'error.retry': 'إعادة المحاولة',
    'error.technical': 'التفاصيل التقنية (للمطوّرين والمختبرين)',
    'state.loading': 'جارٍ التحميل…',
    'state.empty': 'لا توجد بيانات لعرضها.',
    'state.offline': 'أنت غير متصل بالإنترنت.',

    // A nursery's identity (C7): the platform's edit and the nursery's own settings
    'identity.nameAr': 'الاسم بالعربية',
    'identity.nameEn': 'الاسم باللاتينية',
    'identity.address': 'العنوان',
    'identity.contactPhone': 'هاتف الحضانة',
    'identity.contactEmail': 'بريد الحضانة',
    'identity.brandColor': 'لون الحضانة',
    'identity.brandColor.hint': 'اختياري. اكتبه بالصيغة ‎#1A7F64، أو اختره من المربّع.',
    'identity.brandColor.pick': 'اختيار اللون',
    'identity.weekendDays': 'أيام العطلة الأسبوعية',
    'identity.weekendDays.hint': 'لا يُحسب الطفل غائباً في هذه الأيام. اترك يوماً واحداً على الأقل للدوام.',
    'identity.weekend': 'العطلة الأسبوعية: {days}',
    'weekday.Sunday': 'الأحد',
    'weekday.Monday': 'الاثنين',
    'weekday.Tuesday': 'الثلاثاء',
    'weekday.Wednesday': 'الأربعاء',
    'weekday.Thursday': 'الخميس',
    'weekday.Friday': 'الجمعة',
    'weekday.Saturday': 'السبت',

    // Common actions
    'action.save': 'حفظ',
    'action.saving': 'جارٍ الحفظ…',
    'action.cancel': 'إلغاء',
    'action.close': 'إغلاق',
    'action.confirm': 'تأكيد',
    'action.search': 'بحث',
    'action.filter': 'تصفية',
    'action.add': 'إضافة',
    'action.edit': 'تعديل',
    'action.back': 'رجوع',
    'action.next': 'التالي',
    'action.previous': 'السابق',
    'paging.position': '{page} / {total}',
    'action.copy': 'نسخ',
    'action.copied': 'تم النسخ',
    'action.print': 'طباعة',
    'action.printing': 'جارٍ التحضير…',

    // Preferences
    'pref.language': 'اللغة',
    'pref.theme': 'المظهر',
    'pref.theme.light': 'فاتح',
    'pref.theme.dark': 'داكن',
    'pref.theme.system': 'حسب النظام',
    'pref.numerals': 'الأرقام',
    'pref.numerals.western': '123',
    'pref.numerals.eastern': '١٢٣',

    // Domain vocabulary
    'child.one': 'طفل',
    'children.count.zero': 'لا يوجد أطفال',
    'children.count.one': 'طفل واحد',
    'children.count.two': 'طفلان',
    'children.count.few': '{count} أطفال',
    'children.count.many': '{count} طفلاً',
    'children.count.other': '{count} طفل',
    // A child's age (GAPS E24): years, months, or both joined by «و».
    'age.years.one': 'سنة',
    'age.years.two': 'سنتان',
    'age.years.few': '{count} سنوات',
    'age.years.many': '{count} سنة',
    'age.years.other': '{count} سنة',
    'age.months.zero': 'أقل من شهر',
    'age.months.one': 'شهر',
    'age.months.two': 'شهران',
    'age.months.few': '{count} أشهر',
    'age.months.many': '{count} شهراً',
    'age.months.other': '{count} شهر',
    'age.both': '{years} و{months}',
    'class.one': 'صف',
    'classes.count.zero': 'لا توجد صفوف',
    'classes.count.one': 'صف واحد',
    'classes.count.two': 'صفّان',
    'classes.count.few': '{count} صفوف',
    'classes.count.many': '{count} صفاً',
    'classes.count.other': '{count} صف',
    'staff.one': 'موظف',
    // Role names, matching Daghfal.Domain.Authorization.SystemRoles. Used for display only: access is
    // always decided by a permission key, never by a role name (FR-RBAC-01).
    'role.owner': 'المالك',
    'role.manager': 'المدير',
    'role.accountant': 'المحاسب',
    'role.teacher': 'المعلمة',
    'role.assistant': 'مساعدة',
    'role.reception': 'الاستقبال',
    'role.nurse': 'الممرضة',
    'role.guardian': 'ولي الأمر',
    'role.superadmin': 'مدير المنصّة',
    'role.support_employee': 'موظف دعم',
    'role.billing_employee': 'موظف محاسبة',
    'gender.Male': 'ذكر',
    'gender.Female': 'أنثى',

    // Allergies (FR-CHD-05, Q75, Q76). The picture helps; the name the nursery wrote is always the label.
    'allergy.warning.title': 'تحذير حساسية — لا يُقدَّم:',
    'allergy.severity.Mild': 'حساسية خفيفة',
    'allergy.severity.Severe': 'حساسية شديدة',
    'allergy.severity.Anaphylactic': 'حساسية خطيرة تهدّد الحياة',
    'allergy.mark': '{allergen} — {severity}',
    'allergy.marks': 'الحساسية: {list}',
    'allergy.picker.empty': 'لم تُكتب المادة بعد',
    'allergy.picker.suggestions': 'مواد مطابقة في القائمة',
    'allergy.picker.unknown': 'هذه المادة ليست في قائمة المنصّة، فستظهر برمز عام إلى أن تُضاف. اكتب اسم المادة وحدها، مثل «سمسم».',

    // Presence (FR-HOME-02)
    'presence.present': 'موجود/ة الآن في الحضانة',
    'presence.present.male': 'موجود الآن في الحضانة',
    'presence.present.female': 'موجودة الآن في الحضانة',
    'presence.departed': 'غادر/ت الساعة {time}',
    'presence.departed.male': 'غادر الساعة {time}',
    'presence.departed.female': 'غادرت الساعة {time}',
    'presence.notArrived': 'لم يسجل الحضور بعد',
    'presence.notArrived.male': 'لم يسجّل الحضور بعد',
    'presence.notArrived.female': 'لم تسجّل الحضور بعد',
    'presence.absenceReported': 'غياب مُبلَّغ',

    // Statuses
    'status.Trial': 'تجريبي',
    'status.Active': 'فعّال',
    'status.PastDue': 'متأخر السداد',
    'status.Suspended': 'موقوف',
    'status.Archived': 'مؤرشف',
    'status.Enrolled': 'مسجّل',
    'status.Waitlisted': 'قائمة انتظار',
    'status.Withdrawn': 'منسحب',
    'status.Graduated': 'متخرّج',

    // Support session banner (SRS §3.5)
    'support.banner': 'أنت تعمل داخل بيانات حضانة أخرى ضمن جلسة دعم مُصرَّح بها. كل إجراء مُسجَّل.',

    // Pending features
    'pending.title': 'هذه الشاشة قيد التطوير',
    'pending.body': 'الواجهة البرمجية لهذه الميزة ({requirement}) لم تُنشر بعد. ستظهر البيانات هنا فور توفّرها.',
    'fixtures.banner': 'بيانات تجريبية للعرض فقط — ليست بيانات حقيقية.',

    // Statuses added by the care, attendance and finance modules
    'status.Pending': 'قيد الانتظار',
    'status.Draft': 'مسودّة',
    'status.Published': 'منشور',
    'status.Issued': 'صادرة',
    'status.PartiallyPaid': 'مدفوعة جزئياً',
    'status.Paid': 'مدفوعة',
    'status.Overdue': 'متأخرة',
    'status.Void': 'ملغاة',
    'status.Invited': 'مدعو',
    'status.Deactivated': 'موقوف',

    // The day — "يومي اليوم" (§4.5)
    'day.title': 'يومي اليوم',
    'day.today': 'اليوم',
    'day.yesterday': 'أمس',
    'day.previous': 'اليوم السابق',
    'day.next': 'اليوم التالي',
    'day.empty': 'لا توجد تسجيلات لهذا اليوم بعد. ستظهر هنا فور تسجيلها.',
    'day.draft': 'لم تُنشر تسجيلات هذا اليوم بعد.',
    'day.publish': 'نشر اليوم لأولياء الأمور',
    'day.published': 'تم نشر اليوم.',
    'day.recordedBy': 'سجّلها {name}',
    'day.section.attendance': '🕗 الحضور',
    'day.section.meals': '🍳 الوجبات',
    'day.section.drinks': '💧 الماء والمشروبات',
    'day.section.activities': '🎨 الأنشطة',
    'day.section.sleep': '😴 النوم',
    'day.section.mood': '😊 المزاج',
    'day.section.diaper': '🧷 الحفاض / الحمام',
    'day.section.notes': '📝 ملاحظة المعلمة',
    'day.arrived': 'الوصول {time}',
    'day.left': 'المغادرة {time}',
    'day.sleeping': 'نائم/ة الآن منذ {time}',
    'day.sleeping.male': 'نائم الآن منذ {time}',
    'day.sleeping.female': 'نائمة الآن منذ {time}',
    'day.napRange': 'من {from} إلى {to}',
    'day.creamApplied': 'مع كريم',
    'day.photoCount': 'صور هذا اليوم: {count}',

    // Care entries
    'care.kind.Meal': 'وجبة',
    'care.kind.Drink': 'شراب',
    'care.kind.Sleep': 'نوم',
    'care.kind.Activity': 'نشاط',
    'care.kind.Mood': 'مزاج',
    'care.kind.Diaper': 'حفاض',
    'care.kind.Note': 'ملاحظة',
    'care.amount.All': 'أكل كامل الوجبة',
    'care.amount.Most': 'أكل معظم الوجبة',
    'care.amount.Some': 'أكل نصف الوجبة',
    'care.amount.Little': 'أكل قليلاً',
    'care.amount.Refused': 'رفض الطعام',
    'care.mealType': 'نوع الوجبة',
    'care.foodItems': 'الأصناف',
    'care.amountEaten': 'الكمية',
    'care.note': 'ملاحظة',
    'care.saved': 'تم الحفظ.',
    'care.allergyAcknowledge': 'أُقرّ باطّلاعي على تحذير الحساسية أعلاه',
    'care.wholeClass': 'تطبيق على الصف كامل',
    'care.selectedChildren': 'تم اختيار {count}',
    'care.time': 'الوقت',
    'care.time.future': 'لا يمكن تسجيل وقت لم يأتِ بعد. اختر وقتاً مضى.',

    // A child's day as staff keep it (FR-STF-10, FR-DAY-05, FR-DAY-06)
    'careDay.title': 'ما سُجّل اليوم',
    'careDay.titleOn': 'ما سُجّل يوم {date}',
    'careDay.empty': 'لم يُسجَّل شيء لهذا اليوم بعد.',
    'careDay.draft': 'مسودّة',
    'careDay.published': 'منشور',
    'careDay.waitsForReview': 'هذا اليوم بانتظار المراجعة قبل أن يراه الأهل. تنشره المعلّمة المسؤولة أو الإدارة.',
    'careDay.wokeUp': 'إنهاء القيلولة الآن',
    'careDay.delete': 'حذف',
    'careDay.confirmDelete': 'تأكيد الحذف',
    'careDay.editTitle': 'تعديل: {kind}',
    'careDay.sleptAt': 'بداية النوم',
    'careDay.wokeAt': 'الاستيقاظ',

    // Attendance (§4.9)
    'attendance.title': 'الحضور',
    'attendance.board': 'لوحة الحضور',
    'attendance.checkIn': 'تسجيل وصول',
    'arrival.title': 'الفحص عند الوصول',
    'arrival.temperature': 'درجة الحرارة (°م)',
    'arrival.temperature.hint': 'مثل 37.2. اتركها فارغة إن لم تُقَس.',
    'arrival.temperature.invalid': 'اكتب درجة الحرارة رقمًا فيه منزلة عشرية واحدة على الأكثر، مثل 37.2.',
    'arrival.note': 'ما لوحظ عند الوصول',
    'arrival.note.hint': 'سعال أو طفح أو خدش… اتركه فارغًا إن لم يُلاحَظ شيء.',
    'arrival.reading': 'الحرارة {value}°',
    'attendance.checkOut': 'تسجيل مغادرة',
    'attendance.present': 'الحاضرون',
    'attendance.absent': 'الغائبون',
    'attendance.notified': 'غياب مُبلَّغ',
    'attendance.expected': 'لم يصل بعد',
    'attendance.departed': 'غادروا',
    'attendance.closedDay': 'الحضانة مغلقة اليوم، ولذلك لا يُحتسب الغياب.',
    'attendance.correct': 'تصحيح',
    'attendance.correctOf': 'تصحيح أوقات {name}',
    'attendance.correctReason': 'سبب التصحيح',
    'attendance.checkInOf': 'تسجيل وصول {name}',
    'attendance.checkOutOf': 'تسجيل مغادرة {name}',
    'attendance.history': 'سجل الحضور',
    'attendance.previousMonth': 'الشهر السابق',
    'attendance.nextMonth': 'الشهر التالي',
    'attendance.operatingDays': 'أيام الدوام: {count}',
    'attendance.notices': 'إشعارات الغياب',
    'attendance.noNotices': 'لا توجد إشعارات غياب لهذه الأيام.',
    'attendance.noticeSent': 'أُرسل في',
    'attendance.beforeCutoff': 'لم يحن وقت احتساب الغياب بعد.',
    'attendance.collectedBy': 'استلمه {name}',
    'attendance.pickupOverride': 'استلام استثنائي',
    'attendance.choosePickup': 'من يستلم الطفل؟',
    'attendance.pickupOther': 'شخص آخر (يتطلّب ذكر السبب)',
    'attendance.doNotRelease': 'لا يُسلَّم الطفل إلى',
    'attendance.doNotRelease.suspended': 'وليّ أمر موقوف. تواصل مع الإدارة إن حضر.',
    'attendance.doNotRelease.acknowledge': 'قرأت قائمة من لا يُسلَّم إليهم الطفل، وتأكّدت أن المستلم ليس منهم.',
    'attendance.guardians': 'أولياء الأمور',
    'attendance.pickups': 'المفوَّضون بالاستلام',
    'attendance.pickupName': 'اسم المستلم',
    'attendance.pickupReason': 'سبب الاستلام الاستثنائي',
    'attendance.temperature': 'الحرارة (°م)',
    'attendance.symptoms': 'أعراض ظاهرة',
    'attendance.notifyAbsence': 'إبلاغ عن غياب',
    'attendance.absenceFrom': 'من تاريخ',
    'attendance.absenceTo': 'إلى تاريخ',
    'attendance.absenceReason': 'السبب (اختياري)',
    'attendance.absenceSent': 'تم إبلاغ الحضانة بالغياب.',
    'attendance.summary': 'حضور {present} · غياب {absent} · مُبلَّغ {notified}',

    // Photographs (§4.6)
    'photos.title': 'الصور',
    'photos.empty': 'لا توجد صور بعد.',
    'photos.count.one': 'صورة واحدة',
    'photos.count.two': 'صورتان',
    'photos.count.few': '{count} صور',
    'photos.count.many': '{count} صورة',
    'photos.count.other': '{count} صورة',
    'photos.occasion': 'المناسبة',
    'photos.allOccasions': 'كل المناسبات',
    'photos.upload': 'رفع صورة أو فيديو',
    'photos.video': 'فيديو',
    'photos.video.hint': 'الفيديو حتى {mb} ميغابايت. على آيفون اختر «الأكثر توافقاً» من الإعدادات ← الكاميرا ← التنسيقات، حتى يعمل الفيديو على كل الأجهزة.',
    'photos.tooLarge.photo': 'الصورة أكبر من {mb} ميغابايت. اختر صورة أصغر.',
    'photos.tooLarge.video': 'الفيديو أكبر من {mb} ميغابايت. قصّر المقطع أو صوّره بجودة أقل.',
    'photos.visibility': 'من يمكنه رؤيتها',
    'photos.visibility.SingleChild': 'ولي أمر هذا الطفل فقط',
    'photos.visibility.SharedTagged': 'أولياء أمور كل الأطفال في الصورة',
    'photos.caption': 'تعليق',
    'photos.tagged': 'الأطفال في الصورة',
    'photos.uploaded': 'تم رفع الصورة.',

    // Consents (§4.12.1)
    'consent.title': 'الموافقات',
    'consent.hint': 'تسجّلها الإدارة من نموذج الموافقة الموقّع. دون موافقة الصور الفردية لا يُصوَّر الطفل ولا يُشار إليه في أي صورة.',
    'consent.IndividualPhotos': 'الصور الفردية',
    'consent.GroupPhotos': 'الصور الجماعية',
    'consent.Outings': 'الرحلات الخارجية',
    'consent.EmergencyMedicalTreatment': 'العلاج الطبي الطارئ',
    'consent.granted': 'ممنوحة',
    'consent.notGranted': 'غير ممنوحة',
    'consent.unanswered': 'لم تُسجَّل',
    'consent.grant': 'تسجيل الموافقة',
    'consent.refuse': 'تسجيل الرفض',
    'consent.revoke': 'سحب',
    'consent.evidence': 'مرجع النموذج الموقّع',
    'consent.evidence.hint': 'رقم النموذج أو مكان حفظه. مطلوب لتسجيل موافقة.',
    'consent.evidence.required': 'أدخل مرجع النموذج الموقّع قبل تسجيل الموافقة.',
    'consent.onBehalfOf': 'نيابةً عن',
    'consent.onBehalfOf.none': 'لا يذكر النموذج وليّ أمر بعينه',
    'consent.onBehalfOfName': 'نيابةً عن {name}',
    'consent.recordedBy': 'سجّلها {name} في {date}',
    'consent.withdrawnPhotos.zero': 'لم تُسحب أي صورة.',
    'consent.withdrawnPhotos.one': 'سُحبت صورة واحدة من كل المعارض والمحادثات.',
    'consent.withdrawnPhotos.two': 'سُحبت صورتان من كل المعارض والمحادثات.',
    'consent.withdrawnPhotos.few': 'سُحبت {count} صور من كل المعارض والمحادثات.',
    'consent.withdrawnPhotos.many': 'سُحبت {count} صورة من كل المعارض والمحادثات.',
    'consent.withdrawnPhotos.other': 'سُحبت {count} صورة من كل المعارض والمحادثات.',
    'consent.registration': 'موافقات الأسرة',
    'consent.registration.hint': 'كما وقّعتها الأسرة في نموذج التسجيل. ما يُترك دون إجابة يظهر في قائمة «موافقات ناقصة».',
    'consentGaps.title': 'موافقات ناقصة',
    'consentGaps.lede': 'أطفال لم تُسجَّل لهم إجابة عن إحدى الموافقات. لا يُصوَّر طفل قبل تسجيل موافقته على الصور الفردية. افتح صفحة الطفل وسجّل ما في نموذجه الموقّع.',
    'consentGaps.column.unanswered': 'دون إجابة',
    'consentGaps.photographable': 'يمكن تصويره',
    'consentGaps.notPhotographable': 'لا يُصوَّر',
    'consentGaps.empty': 'سُجّلت كل الموافقات لكل الأطفال.',
    'consentGaps.notice': 'عدد الأطفال الذين تنقصهم إجابة عن موافقة: {count}.',
    'consentGaps.open': 'عرض القائمة',
    'photos.noConsent': 'لا موافقة على تصويره',
    'photos.noGroupConsent': 'لا موافقة على الصور الجماعية',
    'photos.childNotPhotographable': 'لم تسجّل الإدارة موافقة أسرة هذا الطفل على الصور الفردية، فلا تُرفع له صورة. اطلب من الإدارة تسجيلها من النموذج الموقّع.',
    'photos.taggedNames': 'في الصورة: {names}',
    'photos.status.Pending': 'لم تُنشر',
    'photos.status.Published': 'منشورة',
    'photos.status.Withdrawn': 'مسحوبة',
    'photos.correct': 'تصحيح',
    'photos.correctPublished': 'الصورة منشورة. يصل التصحيح إلى الأهالي فوراً: من أزلت طفله لا يراها بعد الآن، ومن أضفت طفله يُبلَّغ بها.',
    'photos.untaggable': '{name} (لا موافقة)',
    'photos.republish': 'إعادة النشر',
    'photos.remove': 'حذف',
    'photos.removeWarning': 'تُحذف الصورة من معرض كل الأهالي ولا يمكن استعادتها.',
    'photos.confirmRemove': 'نعم، احذفها',

    // Messaging (§4.7)
    'messages.title': 'الرسائل',
    'messages.empty': 'لا توجد محادثات بعد.',
    'messages.new': 'محادثة جديدة',
    'messages.to': 'إلى',
    'messages.write': 'اكتب رسالتك…',
    'messages.send': 'إرسال',
    'messages.read': 'تمت القراءة',
    'messages.delivered': 'تم التسليم',
    'messages.about': 'بخصوص {name}',
    'messages.attachment': 'مرفق',
    'notifications.bell': 'التنبيهات',
    'notifications.bell.unread': 'التنبيهات، غير المقروءة: {count}',
    'notifications.seeAll': 'عرض كل التنبيهات',
    'messages.unavailable': 'هذه الصورة أو المقطع لم يعد متاحاً.',
    'messages.photo.open': 'فتح الصورة بحجمها الكامل',
    'watermark.text': '{name} · {phone} · {time}',
    'media.fullscreen': 'ملء الشاشة',
    'media.fullscreen.exit': 'الخروج من ملء الشاشة',
    'date.placeholder': 'يوم/شهر/سنة',
    'date.pick': 'اختيار التاريخ من التقويم',
    'date.invalid': 'اكتب التاريخ يومًا ثم شهرًا ثم سنة، مثل 13/5/2025، أو اختره من التقويم.',
    'date.tooEarly': 'اختر تاريخًا من {date} أو بعده.',
    'date.tooLate': 'اختر تاريخًا حتى {date}.',
    'messages.image.send': 'إرسال صورة',
    'messages.image.hint': 'صورة وصفة طبية أو حالة تريد أن تُريها للحضانة. يُرسل ما تكتبه في خانة الرسالة معها.',
    'messages.image.unreadable': 'تعذّر فتح هذا الملف كصورة. اختر صورة بصيغة JPEG أو PNG، أو التقطها بالكاميرا مباشرة.',
    'messages.image.tooLarge': 'الصورة كبيرة جداً حتى بعد تصغيرها. الحد الأقصى {mb} ميغابايت. اختر صورة أصغر.',
    'messages.delete': 'حذف',
    'messages.delete.confirm': 'تأكيد الحذف',
    'messages.voice.hold': 'اضغط مطوّلاً للتسجيل',
    'messages.voice.release': 'أفلِت للإرسال · {time}',
    'messages.voice.hint': 'سيطلب المتصفح في المرة الأولى إذناً باستخدام الميكروفون. اسمح به، ثم اضغط مطوّلاً وتكلّم.',
    'messages.voice.ready': 'تم السماح بالميكروفون. اضغط الآن مطوّلاً وتكلّم، ثم أفلِت للإرسال.',
    'messages.voice.denied': 'لم يُسمح باستخدام الميكروفون. اسمح به لهذا الموقع من إعدادات المتصفح، ثم حاول مرة أخرى.',
    'messages.voice.noMicrophone': 'لم نجد ميكروفوناً في هذا الجهاز. صِل ميكروفوناً ثم حاول مرة أخرى.',
    'messages.voice.unsupported': 'هذا المتصفح لا يسجّل الصوت. حدّث المتصفح أو استخدم Chrome أو Safari.',
    'messages.voice.failed': 'تعذّر بدء التسجيل. أغلق التطبيقات الأخرى التي تستخدم الميكروفون ثم حاول مرة أخرى.',
    'messages.voice.tooShort': 'التسجيل قصير جداً. استمرّ بالضغط أثناء الكلام.',
    'messages.voice.limit': 'توقّف التسجيل عند {duration}، وهي أطول مدة للرسالة الصوتية، وأُرسلت الرسالة.',
    'messages.voice.label': 'رسالة صوتية، مدتها {time}',
    'messages.voice.unplayable': 'لا يستطيع هذا المتصفح تشغيل هذه الرسالة الصوتية ({time}). افتح المحادثة من متصفح آخر لسماعها.',
    'messages.share': 'مشاركة من المعرض',
    'messages.share.empty': 'لا توجد صور أو مقاطع منشورة لهذا الطفل يمكن مشاركتها هنا.',
    'messages.share.send': 'إرسال المحدَّد ({count} من {max})',
    'messages.share.limit': 'يمكنك اختيار {max} من العناصر على الأكثر في الرسالة الواحدة.',

    // Announcements and the calendar (§4.7.5)
    'announcements.title': 'الإعلانات',
    'announcements.new': 'إعلان جديد',
    'announcements.subject': 'العنوان',
    'announcements.body': 'النص',
    'announcements.audience': 'الجمهور',
    'announcements.audience.AllGuardians': 'كل أولياء الأمور',
    'announcements.audience.ClassGuardians': 'أولياء أمور صف محدّد',
    'announcements.audience.SpecificGuardian': 'ولي أمر محدّد',
    'announcements.audience.AllStaff': 'كل الموظفين',
    'announcements.schedule': 'جدولة الإرسال',
    'announcements.scheduleTime': 'وقت الإرسال',
    'announcements.sendNow': 'إرسال الآن',
    'announcements.delivery': 'وصل إلى {delivered} من {total} · قرأه {read}',
    'announcements.empty': 'لا توجد إعلانات.',
    'calendar.title': 'التقويم',
    'calendar.new': 'مناسبة جديدة',
    'calendar.type.Closure': 'إغلاق',
    'calendar.type.Event': 'فعالية',
    'calendar.type.Holiday': 'عطلة',
    'calendar.type.Trip': 'رحلة',
    'calendar.affectsAttendance': 'تُستثنى من احتساب الغياب',
    'calendar.empty': 'لا توجد مناسبات قادمة.',
    'calendar.remove': 'حذف',
    'calendar.removeOf': 'حذف {title}',
    'calendar.confirmRemove': 'تأكيد الحذف',
    'calendar.editOf': 'تعديل {title}',
    'calendar.removeWarning': 'ستُحتسب هذه الأيام في الغياب من جديد.',
    'calendar.changeWarning': 'تعديل الإغلاق أو العطلة يغيّر الأيام التي يُحتسب فيها الغياب، في لوحات الحضور ولوحة البيانات.',
    'calendar.hint': 'الإغلاقات والعطل والفعاليات والرحلات القادمة، كما سجّلتها الإدارة.',
    'calendar.range': '{from} – {to}',
    'calendar.forClass': 'لصف {name}',
    'calendar.closed': 'الحضانة مغلقة',

    // Fees, invoices and payments (§4.8)
    'payments.title': 'الرسوم',
    'payments.currentFee': 'رسوم الفترة الحالية',
    'payments.paid': 'المدفوع',
    'payments.remaining': 'المتبقي',
    'payments.credit': 'رصيد لصالحك',
    'payments.lastPayment': 'آخر دفعة: {amount} بتاريخ {date}',
    'payments.noPayments': 'لا توجد دفعات مسجّلة.',
    'payments.history': 'سجل الدفعات',
    'payments.plan': 'خطة الرسوم',
    'payments.method': 'طريقة الدفع',
    'payments.method.Cash': 'نقداً',
    'payments.method.BankTransfer': 'حوالة مصرفية',
    'payments.method.Cheque': 'شيك',
    'payments.method.Other': 'أخرى',
    'payments.record': 'تسجيل دفعة',
    'payments.amount': 'المبلغ',
    'payments.date': 'التاريخ',
    'payments.reference': 'رقم المرجع',
    'payments.paidBy': 'من دفع',
    'payments.paidBy.none': 'غير محدَّد',
    'payments.yearTotal': 'رسوم السنة {year}',
    'payments.deadline': 'آخر موعد للسداد: {date}',
    'payments.recorded': 'تم تسجيل الدفعة.',
    'payments.void': 'إلغاء الدفعة',
    'payments.voidReason': 'سبب الإلغاء',
    'invoices.title': 'الفواتير',
    'invoices.number': 'رقم الفاتورة',
    'invoices.period': 'الفترة',
    'invoices.dueDate': 'تاريخ الاستحقاق',
    'invoices.total': 'الإجمالي',
    'invoices.outstanding': 'المتبقي',
    'invoices.generate': 'إصدار فواتير الفترة',
    'invoices.generated': 'تم إنشاء {created} فاتورة، وتخطّي {skipped}.',
    'invoices.issue': 'إصدار',
    'invoices.void': 'إلغاء',
    'finance.void.reason': 'سبب الإلغاء',
    'finance.void.confirm': 'تأكيد الإلغاء',
    'finance.void.invoiceOf': 'إلغاء الفاتورة {number}',
    'finance.void.paymentOf': 'إلغاء دفعة {name}',
    'finance.void.invoiceHint': 'تبقى الفاتورة في السجل مشطوبة، وتصير دفعاتها رصيداً لصالح الأسرة.',
    'finance.void.paymentHint': 'تبقى الدفعة في السجل مشطوبة، ويعود ما سدّدته إلى المتبقي على الطفل.',
    'finance.voided': 'أُلغيت: {reason}',
    'invoices.daysOverdue': 'متأخرة {days} يوماً',
    'invoices.empty': 'لا توجد فواتير.',
    'finance.overdue': 'المتأخرات',
    'finance.overdueTotal': 'إجمالي المتأخرات: {amount}',
    'finance.income': 'تقرير الدخل',
    'finance.incomeTotal': 'المقبوضات',
    'finance.previousPeriod': 'الفترة السابقة',
    'finance.byClass': 'حسب الصف',
    'finance.byMethod': 'حسب طريقة الدفع',
    'finance.sendReminders': 'إرسال تذكير',
    'finance.remindersSent': 'أُرسل {sent} تذكيراً، وتخطّي {skipped}.',
    'finance.from': 'من',
    'finance.to': 'إلى',

    // Health and safety (§4.12)
    'health.title': 'الصحة والسلامة',
    'health.record': 'السجل الصحي',
    'health.bloodType': 'فصيلة الدم',
    'health.chronic': 'أمراض مزمنة',
    'health.dietary': 'قيود غذائية',
    'health.doctor': 'الطبيب',
    'health.doctorPhone': 'هاتف الطبيب',
    'health.vaccinations': 'اللقاحات',
    'health.napHabits': 'عادات النوم',
    'health.comfortItem': 'ما يهدّئه',
    'health.toiletStage': 'مرحلة التدريب على الحمام',
    'health.regularMedication': 'أدوية دائمة',
    'health.notes': 'ملاحظات أخرى',
    'health.hint': 'ما أبلغت به الأسرة الحضانة. يحفظه المسؤول عن الصحة.',
    'health.empty': 'لم يُسجَّل شيء بعد.',
    'incidents.title': 'الحوادث',
    'incidents.printOf': 'طباعة سجل حادث {name}',
    'incidents.new': 'تسجيل حادث',
    'incidents.type': 'النوع',
    'incidents.type.Fall': 'سقوط',
    'incidents.type.Bump': 'ارتطام',
    'incidents.type.Bite': 'عضّة',
    'incidents.type.Illness': 'وعكة صحية',
    'incidents.type.Behavioural': 'سلوكي',
    'incidents.type.Other': 'أخرى',
    'incidents.severity': 'الشدّة',
    'incidents.severity.Minor': 'بسيط',
    'incidents.severity.Moderate': 'متوسط',
    'incidents.severity.Serious': 'خطير',
    'incidents.location': 'المكان',
    'incidents.description': 'ماذا حدث',
    'incidents.bodyArea': 'موضع الإصابة',
    'incidents.actionTaken': 'الإجراء المتّخذ',
    'incidents.firstAid': 'أُسعف',
    'incidents.acknowledge': 'اطّلعت على التقرير',
    'incidents.acknowledged': 'تم الاطّلاع في {date}',
    'incidents.awaiting': 'بانتظار اطّلاع ولي الأمر',
    'incidents.empty': 'لا توجد حوادث مسجّلة.',
    'incidents.newHint': 'يُبلَّغ أولياء أمر الطفل فور الحفظ، ويُطلب منهم الاطّلاع على التقرير.',
    'incidents.date': 'التاريخ',
    'incidents.when': 'متى',
    'incidents.firstAidDetail': 'ما الإسعاف الذي قُدّم',
    'incidents.firstAidYes': 'نعم',
    'incidents.witness': 'شاهد من الفريق',
    'incidents.noWitness': 'لا يوجد',
    'incidents.reportedBy': 'سجّله',
    'incidents.record': 'تسجيل الحادث',
    'incidents.recorded': 'سُجّل الحادث وأُبلغ أولياء الأمر.',
    'incidents.of': 'حادث: {name}',
    'incidents.open': 'فتح السجل',
    'incidents.openToClose': 'فتح السجل وإغلاقه',
    'incidents.close': 'إغلاق السجل',
    'incidents.confirmClose': 'تأكيد الإغلاق',
    'incidents.closed': 'أُغلق السجل في {date}',
    'incidents.outcome': 'النتيجة',
    'incidents.outcome.ResolvedOnSite': 'عولج في الحضانة',
    'incidents.outcome.SeenByDoctor': 'عُرض على طبيب',
    'incidents.outcome.TakenToHospital': 'نُقل إلى المستشفى',
    'incidents.outcome.TakenHomeByGuardian': 'أخذه وليّ الأمر إلى البيت',
    'incidents.followUp': 'ما حدث بعد ذلك',
    'incidents.followUp.hint': 'يقرأ وليّ الأمر ما تكتبه هنا.',
    'incidents.prevention': 'ما غيّرناه لتجنّب تكرارها',
    'incidents.prevention.hint': 'للعاملين فقط، ولا يراه وليّ الأمر.',
    'incidents.toldBy': 'كيف أُبلغ وليّ الأمر؟',
    'incidents.toldBy.hint': 'لم يؤكّد وليّ الأمر اطلاعه على الحادثة في التطبيق.',
    'incidents.toldBy.PhoneCall': 'باتصال هاتفي',
    'incidents.toldBy.InPerson': 'حضوريًا',
    'incidents.unread': 'لم يُطّلع عليه',
    'incidents.history': 'الحوادث المسجّلة',
    'medication.title': 'الأدوية',
    'medication.name': 'اسم الدواء',
    'medication.dose': 'الجرعة',
    'medication.schedule': 'مواعيد الإعطاء',
    'medication.maxPerDay': 'الحد الأقصى يومياً',
    'medication.authorize': 'تسجيل دواء',
    'medication.hint': 'تسجّله الإدارة كما أبلغتها الأسرة. لا يُدخل وليّ الأمر أي دواء.',
    'medication.administer': 'تسجيل إعطاء جرعة',
    'medication.dosesToday': 'أُعطيت اليوم: {count}',
    'medication.dosesOfMax': 'أُعطيت اليوم: {count} من {max}',
    'medication.doseGiven': 'الجرعة المعطاة',
    'medication.confirmGiven': 'أُعطيت الجرعة',
    'medication.given': 'سُجّلت الجرعة وأُبلغ أولياء الأمر.',
    'medication.overLimit': 'بلغت جرعات اليوم الحد الأقصى المسجّل. اكتب سبب إعطاء جرعة أخرى.',
    'medication.reason': 'السبب',
    'medication.noneGiven': 'لم تُعطَ أي جرعة بعد.',
    'medication.givenList': 'الجرعات المعطاة',
    'medication.givenBy': 'أعطتها {name}',
    'medication.outOfSchedule': 'خارج الموعد: {reason}',
    'medication.dueTitle': 'أدوية اليوم',
    'medication.dueEmpty': 'لا توجد أدوية سارية اليوم لأطفال هذا الصف.',
    'medication.empty': 'لا توجد أدوية مسجّلة.',
    'medication.form': 'شكل الدواء',
    'medication.form.choose': 'اختر شكل الدواء',
    'medication.dose.hint': 'كما تُكتب: ٥، نصف حبة، نقطتان.',
    'medication.doseWithUnit': '{dose} {unit}',
    'medication.doseIn': 'الجرعة ({unit})',
    'medication.period': 'من {from} حتى {to}',
    'medication.since': 'منذ {from}',
    'medication.startDate': 'من تاريخ',
    'medication.endDate': 'حتى تاريخ',
    'medication.notes': 'ملاحظات',
    'medication.informedBy': 'أبلغ به',
    'medication.informedBy.none': 'لا أحد بعينه (نموذج موقّع)',
    'medication.recordedBy': 'سجّله {name}',
    'medication.informedByName': 'أبلغ به {name}',
    'medication.current': 'ساري',
    'medication.ended': 'منتهٍ',
    'medication.required': 'أدخل اسم الدواء وشكله والجرعة ومواعيد الإعطاء.',

    // Staff administration (§4.11.4)
    'staff.title': 'الموظفون',
    'staff.invite': 'دعوة موظف',
    'staff.name': 'الاسم',
    'staff.phone': 'رقم الهاتف',
    'people.numberReleased': 'انتقل الرقم إلى شخص آخر',
    'nav.accountRequests': 'طلبات الحسابات',
    'accountRequests.title': 'طلبات استعادة الحسابات',
    'accountRequests.lede': 'أشخاص طلبوا إعادة تعيين كلمة السر بعد مدة طويلة دون استخدام حساباتهم. تأكّد من كل شخص وجهاً لوجه قبل الموافقة.',
    'accountRequests.empty': 'لا طلبات تنتظر.',
    'accountRequests.requestedAt': 'تاريخ الطلب',
    'accountRequests.from': 'من أين',
    'accountRequests.approve': 'تأكدتُ منه شخصياً',
    'accountRequests.reject': 'رفض',
    'accountRequests.approved': 'يستطيع الآن تعيين كلمة سر جديدة برمز يصل إلى رقمه.',
    'accountRecovery.action': 'الحساب والرقم',
    'accountRecovery.title': 'الحساب والرقم — {name}',
    'accountRecovery.renumber.title': 'نقل الحساب إلى رقم جديد',
    'accountRecovery.renumber.hint': 'لمن فقد رقمه. يصل رابط إلى الرقم الجديد، وينتقل الحساب حين يُفتح، ثم يعيّن صاحبه كلمة سر جديدة برمز.',
    'accountRecovery.renumber.phone': 'الرقم الجديد',
    'accountRecovery.renumber.submit': 'أرسل الرابط',
    'accountRecovery.renumber.sent': 'أُرسل الرابط إلى {phone}، وينتهي في {date}.',
    'accountRecovery.release.title': 'فصل الرقم عن الحساب',
    'accountRecovery.release.hint': 'حين يصير الرقم لشخص آخر. يبقى الحساب باسمه وسجلاته، ولا يستطيع أحد الدخول إليه بعد اليوم.',
    'accountRecovery.release.confirm': 'تأكدتُ أن الرقم لم يعد لهذا الشخص',
    'accountRecovery.release.submit': 'افصل الرقم',
    'accountRecovery.release.done': 'فُصل الرقم عن الحساب.',
    'staff.role': 'الدور',
    'staff.jobTitle': 'المسمّى الوظيفي',
    'staff.classes': 'الصفوف',
    'staff.permissions': 'الصلاحيات',
    'staff.deactivate': 'إيقاف الحساب',
    'staff.deactivated': 'تم إيقاف الحساب.',
    'staff.permissionFromRole': 'من الدور',
    'staff.permissionBlocked': 'لا يمكن منحها لهذا الدور',
    'staff.empty': 'لا يوجد موظفون.',
    'staff.editOf': 'تعديل بيانات {name}',
    'staff.details': 'الاسم والدور والمسمّى',
    'staff.role.hint': 'يتغيّر ما يستطيع فعله بتغيير الدور، من طلبه التالي.',
    'staff.saved': 'تم الحفظ.',
    'staff.classes.hint': 'الصف المحدّد بتاريخين تغطية لزميلة غائبة، ومن دون تاريخين هو صفها الدائم.',
    'staff.classes.none': 'لا يوجد صف بعد.',
    'staff.roleInClass': 'الدور في الصف',
    'staff.roleInClass.Lead': 'معلّمة الصف',
    'staff.roleInClass.Assistant': 'مساعدة',
    'staff.roleInClass.Substitute': 'تغطية',
    'staff.assignment.span': 'من {from} حتى {to}',
    'staff.assign': 'إضافة الصف',
    'staff.assign.from': 'من تاريخ',
    'staff.assign.to': 'حتى تاريخ',
    'staff.assign.coverHint': 'اتركه فارغاً لصف دائم، وحدّده للتغطية.',
    'staff.unassign': 'إنهاء',
    'staff.unassignFrom': 'إنهاء صف {name}',
    'staff.inviteAgain': 'دعوة من جديد',
    'staff.inviteAgainOf': 'دعوة {name} من جديد',
    'staff.inviteAgain.hint': 'قبول هذه الدعوة يعيد فتح حسابه بالدور المختار هنا. لا تعود صلاحياته وصفوفه السابقة: امنحها من جديد بعد القبول.',

    // Guardians and pickups (§4.12.2, §4.12.4)
    'guardians.title': 'أولياء الأمور',
    'guardians.add': 'إضافة ولي أمر',
    'guardians.primary': 'ولي الأمر الأساسي',
    'guardians.relationship': 'صلة القرابة',
    'guardians.relationship.Mother': 'الأم',
    'guardians.relationship.Father': 'الأب',
    'guardians.relationship.Grandmother': 'الجدّة',
    'guardians.relationship.Grandfather': 'الجدّ',
    'guardians.relationship.Aunt': 'الخالة/العمة',
    'guardians.relationship.Uncle': 'الخال/العم',
    'guardians.relationship.Sibling': 'أخ/أخت',
    'guardians.relationship.LegalGuardian': 'وصيّ قانوني',
    'guardians.relationship.Other': 'أخرى',
    'pickups.title': 'المفوَّضون بالاستلام',
    'pickups.add': 'إضافة شخص مفوَّض',
    'pickups.validFrom': 'صالح من',
    'pickups.validTo': 'صالح حتى',
    'pickups.expired': 'انتهى التفويض',
    'pickups.empty': 'لا يوجد مفوَّضون غير أولياء الأمور.',
    'pickups.hint': 'أولياء الأمور مخوَّلون بالاستلام دائمًا. أضف هنا من يستلم الطفل غيرهم، بعد أن تُعلم الأسرةُ الحضانة.',
    'pickups.name': 'الاسم',
    'pickups.name.required': 'أدخل اسم الشخص.',
    'pickups.relationship': 'صلته بالطفل',
    'pickups.idDocument': 'رقم الهوية',
    'pickups.remove': 'إلغاء التفويض',
    'pickups.validity': 'من {from} حتى {to}',

    // Notifications (§4.13)
    'notifications.title': 'الإشعارات',
    'notifications.empty': 'لا توجد إشعارات.',
    'notifications.markAllRead': 'تعليم الكل كمقروء',
    'notifications.preferences': 'تفضيلات الإشعارات',
    'notifications.mandatory': 'لا يمكن إيقافه',
    'notifications.digested': 'مُجمَّع',
    'notifications.quietHours': 'ساعات الهدوء',

    // Each notification type by its code, on the preferences screen. The server's description is English and
    // only the fallback; `NotificationTypeLabelsTests` fails on a type the preferences list and this omits.
    'notifications.type.ChildCheckedIn': 'وصل الطفل بسلام',
    'notifications.type.ChildCheckedOut': 'غادر الطفل الحضانة',
    'notifications.type.IncidentRecorded': 'سُجّلت حادثة تخصّ الطفل',
    'notifications.type.UnauthorizedPickupOverride': 'سُلّم الطفل لشخص ليس في قائمة المفوَّضين',
    'notifications.type.PickupListChanged': 'تغيّرت قائمة من يحقّ لهم استلام الطفل',
    'notifications.type.DirectMessageReceived': 'وصلتك رسالة',
    'notifications.type.AnnouncementPublished': 'نُشر إعلان',
    'notifications.type.InvoiceIssued': 'صدرت فاتورة',
    'notifications.type.PaymentDue': 'حان موعد دفعة',
    'notifications.type.InvoiceOverdue': 'فاتورة متأخرة السداد',
    'notifications.type.PaymentRecorded': 'سُجّلت دفعة',
    'notifications.type.MedicationAdministered': 'أُعطي الطفل دواءً مأذوناً به',
    'notifications.type.AbsenceNoticeReceived': 'أبلغ وليّ أمر عن غياب',
    'notifications.type.AbsenceNoticeWithdrawn': 'سحب وليّ أمر بلاغ غياب',
    'notifications.type.SupportSessionRequested': 'طلب أحد موظفي المنصّة الاطّلاع على بيانات الحضانة',
    'notifications.type.SupportSessionApproved': 'وُوفق على جلسة دعم وهي نشطة الآن',
    'notifications.type.SupportSessionWrite': 'أُجري تعديل خلال جلسة دعم نشطة',
    'notifications.type.SupportSessionEnded': 'انتهت جلسة دعم',
    'notifications.type.InvitationSent': 'أُرسلت دعوة للانضمام',
    'notifications.type.PlanLimitReached': 'بُلغ أحد حدود الباقة',
    'notifications.type.TrialExpiring': 'الفترة التجريبية توشك على الانتهاء',
    'notifications.type.ComplaintSubmitted': 'قدّم وليّ أمر شكوى',
    'notifications.type.ComplaintAssigned': 'أُسندت إليك شكوى',
    'notifications.type.ComplaintUpdated': 'استُلمت شكواك أو رُدّ عليها أو حُلّت',
    'notifications.type.ComplaintReplyReceived': 'ردّ وليّ أمر على شكوى',
    'notifications.type.AttendanceNotRecorded': 'أطفال لم يُسجَّل حضورهم ولا غيابهم وموعد الإغلاق قريب',
    'notifications.type.PhotosPublished': 'نُشرت صور جديدة',
    'notifications.type.DailyCareUpdated': 'سُجّلت أنشطة رعاية جديدة',
    'notifications.type.EndOfDaySummary': 'ملخّص اليوم',
    'notifications.type.UpcomingClosureOrEvent': 'إغلاق أو مناسبة قريبة',
    'notifications.type.PaymentUpcoming': 'موعد دفعة يقترب',

    // Admin dashboard (§4.11.1)
    'dashboard.title': 'لوحة المتابعة',
    'metric.children.registered': '👶 الأطفال المسجّلون',
    'metric.attendance.present': '🟢 الحاضرون اليوم',
    'metric.attendance.absent': '🔴 الغائبون اليوم',
    'metric.attendance.notified': '🟡 غياب مُبلَّغ',
    'metric.attendance.departed': '👋 غادروا',
    'metric.daily.missing': '📔 بلا تسجيلات اليوم',
    'metric.messages.unread': '💬 رسائل غير مقروءة',
    'metric.incidents.open': '🚑 حوادث بانتظار الاطّلاع',
    'metric.staff.onDuty': '🧑‍🏫 الموظفون اليوم',
    'metric.finance.invoiced': '💰 فواتير هذا الشهر',
    'metric.finance.collected': '💵 المحصّل هذا الشهر',
    'metric.finance.outstanding': '⚠️ إجمالي المتبقي',
    'metric.finance.overdue30': '⏰ متأخرة أكثر من ٣٠ يوماً',

    // Tenant settings (§3.6)
    'settings.title': 'الإعدادات',
    'settings.saved': 'تم حفظ الإعدادات.',
    'settings.reviewOff.warning': 'عند الحفظ تُنشَر لأولياء الأمور كل الأيام التي تنتظر المراجعة، عدا أيام الفصول المُقفَلة.',
    'settings.draftsPublished.one': 'نُشر يوم واحد كان ينتظر المراجعة.',
    'settings.draftsPublished.two': 'نُشر يومان كانا ينتظران المراجعة.',
    'settings.draftsPublished.few': 'نُشرت {count} أيام كانت تنتظر المراجعة.',
    'settings.draftsPublished.many': 'نُشر {count} يوماً كانت تنتظر المراجعة.',
    'settings.draftsPublished.other': 'نُشر {count} يوم كان ينتظر المراجعة.',
    'settings.draftsHeld.one': 'بقي يوم واحد بانتظار المراجعة لأنه في فصل مُقفَل. يُنشَر من صفحة الطفل بعد إعادة فتح الفصل.',
    'settings.draftsHeld.two': 'بقي يومان بانتظار المراجعة لأنهما في فصل مُقفَل. يُنشَران من صفحة الطفل بعد إعادة فتح الفصل.',
    'settings.draftsHeld.few': 'بقيت {count} أيام بانتظار المراجعة لأنها في فصول مُقفَلة. تُنشَر من صفحة الطفل بعد إعادة فتح الفصل.',
    'settings.draftsHeld.many': 'بقي {count} يوماً بانتظار المراجعة لأنها في فصول مُقفَلة. تُنشَر من صفحة الطفل بعد إعادة فتح الفصل.',
    'settings.draftsHeld.other': 'بقي {count} يوم بانتظار المراجعة لأنها في فصول مُقفَلة. تُنشَر من صفحة الطفل بعد إعادة فتح الفصل.',
    'settings.default': 'القيمة الافتراضية',
    'settings.range': 'من {min} إلى {max}',
    'settings.daily.review_required': 'مراجعة تسجيلات اليوم قبل نشرها',
    'settings.attendance.cutoff_time': 'وقت احتساب الغياب',
    'settings.attendance.cutoff_alert_minutes': 'تنبيه قبل وقت احتساب الغياب (بالدقائق، صفر لإيقافه)',
    'settings.attendance.pickup_required': 'إلزام تحديد المستلم عند المغادرة',
    'settings.attendance.health_check': 'فحص صحي عند الوصول',
    'settings.daily.require_check_in': 'لا يُسجَّل شيء لطفل لم يُسجَّل وصوله',
    'settings.photos.default_visibility': 'الوضع الافتراضي لرؤية الصور',
    'settings.photos.retention_months': 'مدة الاحتفاظ بالصور (أشهر)',
    'settings.notifications.digest_window_minutes': 'نافذة تجميع الإشعارات (دقائق)',
    'settings.notifications.sms_enabled': 'إرسال التنبيهات المهمّة برسالة نصية (الحوادث، والتسليم لشخص غير مخوَّل، وتذكير الدفع والتأخّر فيه). لكل رسالة تكلفة.',
    'settings.messages.attachments_enabled': 'السماح بالصور والفيديو والرسائل الصوتية في المحادثات',
    'settings.notifications.closing_time': 'وقت إغلاق الحضانة',
    'settings.finance.due_day_of_month': 'يوم استحقاق الفاتورة',
    'settings.finance.invoice_number_prefix': 'بادئة رقم الفاتورة',
    'settings.finance.generation_day': 'يوم إصدار الفواتير تلقائياً',
    'settings.reminders.send_time': 'وقت إرسال التذكيرات',
    'settings.reminders.payment_due_schedule': 'التذكير بموعد السداد قبله بـ (أيام مفصولة بفواصل، مثل 30,14,7,3,1؛ فارغ لإيقافه)',
    'settings.reminders.payment_overdue_every_days': 'التذكير بعد انقضاء موعد السداد كل (أيام، 0 لإيقافه)',
    'settings.reminders.payment_sms_last_steps': 'عدد التذكيرات الأخيرة قبل الموعد التي تُرسل برسالة SMS أيضاً',
    'settings.finance.deadline_term': 'موعد سداد رسوم السنة: نهاية الفصل رقم',
    'settings.reminders.calendar_event_days': 'التذكير بالإغلاق أو الفعالية قبلها بـ (أيام، 0 لإيقافه)',
    'settings.exports.window_days': 'مهلة إعداد نسخة بيانات الطفل بعد إنهاء تسجيله (أيام، 0 لأولياء الأمور الحاليين فقط)',
    'settings.exports.file_days': 'مدة حفظ ملف نسخة البيانات لتنزيله (أيام)',
    'settings.retention.daily_care_months': 'حذف التقارير اليومية للطفل بعد مغادرته بـ (أشهر)',
    'settings.retention.attendance_months': 'حذف سجلات حضور الطفل بعد مغادرته بـ (أشهر)',
    'settings.retention.finance_months': 'حذف فواتير الطفل ودفعاته بعد مغادرته بـ (أشهر)',

    // Audit (§4.11.7)
    'audit.title': 'سجل التدقيق',
    'audit.actor': 'المستخدم',
    'audit.entity': 'السجل',
    'audit.action': 'الإجراء',
    'audit.when': 'التاريخ',
    'audit.empty': 'لا توجد سجلات مطابقة.',

    // Bulk import (§4.11.8)
    'import.title': 'استيراد الأطفال',
    'import.hint': 'ارفع ملف CSV يحتوي الأعمدة: الاسم، الكنية، تاريخ الميلاد، الجنس، الصف، تاريخ الالتحاق، اسم ولي الأمر، هاتف ولي الأمر، اسم جهة اتصال الطوارئ، هاتفها، صلتها بالطفل، الحساسية («لا يوجد» أو المواد مفصولة بفواصل). جهة الطوارئ والحساسية مطلوبتان.',
    'import.choose': 'اختيار ملف',
    'import.validate': 'فحص الملف',
    'import.commit': 'تأكيد الاستيراد',
    'import.summary': '{valid} صف صالح من {total} · {invalid} غير صالح',
    'import.blocked': 'صحّح الأخطاء أولاً — لا يتم استيراد ملف جزئياً.',
    'import.done': 'تم استيراد {count} طفلاً.',

    // Complaints (§4.14). The vocabulary both apps render, so it lives here; each app keeps its own
    // wording for the screens themselves, because a parent and an administrator are not owed the same
    // sentences about the same record.
    'complaint.reference': 'رقم الشكوى',
    'complaint.category': 'الموضوع',
    'complaint.category.StaffConduct': 'سلوك موظّف',
    'complaint.category.CareQuality': 'مستوى الرعاية',
    'complaint.category.Safety': 'السلامة',
    'complaint.category.Food': 'الطعام',
    'complaint.category.Facilities': 'المرافق والنظافة',
    'complaint.category.Billing': 'الرسوم والفواتير',
    'complaint.category.Communication': 'التواصل والإبلاغ',
    'complaint.category.Other': 'موضوع آخر',

    'complaint.status': 'الحالة',
    'complaint.status.Submitted': 'وصلت',
    'complaint.status.Acknowledged': 'قيد النظر',
    'complaint.status.Assigned': 'قيد المتابعة',
    'complaint.status.InProgress': 'قيد المعالجة',
    'complaint.status.Resolved': 'تمّت معالجتها',
    'complaint.status.Closed': 'مغلقة',
    'complaint.status.Withdrawn': 'مسحوبة',

    'complaint.severity': 'الأولوية',
    'complaint.severity.Low': 'منخفضة',
    'complaint.severity.Normal': 'عادية',
    'complaint.severity.High': 'عاجلة',

    'complaint.event.Submitted': 'قُدّمت الشكوى',
    'complaint.event.Acknowledged': 'أكّدت الإدارة الاستلام',
    'complaint.event.Assigned': 'أُسندت المتابعة إلى {name}',
    'complaint.event.Reassigned': 'نُقلت المتابعة إلى {name}',
    'complaint.event.InternalNote': 'ملاحظة داخلية',
    'complaint.event.ReplyToGuardian': 'ردّ من الإدارة',
    'complaint.event.GuardianReply': 'ردّ وليّ الأمر',
    'complaint.event.SeverityChanged': 'تغيّرت الأولوية',
    'complaint.event.StatusChanged': 'تغيّرت الحالة',
    'complaint.event.Resolved': 'خلاصة المعالجة',
    'complaint.event.Reopened': 'أُعيد فتح الشكوى',
    'complaint.event.Closed': 'أُغلقت الشكوى',
    'complaint.event.Withdrawn': 'سحب وليّ الأمر الشكوى',

    ...accountStrings.ar,
  },

  en: {
    'a11y.skipToContent': 'Skip to content',
    'a11y.mainNavigation': 'Main navigation',
    'nav.menu': 'Menu',

    'app.daghfal': 'Daghfal',
    'app.tagline': 'Nursery and kindergarten management',

    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.phone': 'Phone number',
    'auth.phone.hint': 'Enter your number in international form, e.g. +963912345678',
    'auth.password': 'Password',
    'auth.password.show': 'Show password',
    'auth.password.hide': 'Hide password',
    'auth.submit': 'Sign in',
    'auth.submitting': 'Signing in…',
    'auth.phone.required': 'Enter your phone number.',
    'auth.password.required': 'Enter your password.',
    'auth.welcome': 'Welcome, {name} 🌷',

    'auth.chooseMembership': 'Choose a nursery',
    'auth.chooseMembership.hint': 'Your account belongs to more than one place. Choose which to open now.',
    'auth.membership.platform': 'Platform administration',
    'auth.membership.opensIn.guardian': 'Opens in the guardian app',
    'auth.membership.opensIn.staff': 'Opens in the staff app',
    'auth.membership.opensIn.admin': 'Opens in the nursery admin console',
    'auth.membership.opensIn.platform': 'Opens in the platform console',
    'auth.membership.elsewhere.hint': 'This account is not used in this app. Open the app named above, or sign in with another account.',
    'auth.signInAgain': 'Sign in again',
    'auth.switchMembership': 'Switch',

    'auth.invitation.title': 'Accept invitation',
    'auth.invitation.newPassword': 'New password',
    'auth.invitation.confirmPassword': 'Confirm password',
    'auth.invitation.mismatch': 'The passwords do not match.',
    'auth.invitation.accept': 'Join',

    'session.title': 'Active devices',
    'session.signOutAll': 'Sign out of every device',
    'session.startedAt': 'Started {date}',
    'session.expiresAt': 'Expires {date}',

    'error.title': 'Something went wrong',
    'error.network': 'Could not reach the server. Check your connection and try again.',
    'error.unexpected': 'An unexpected error occurred. Try again; if it persists, contact support.',
    'error.forbidden': 'You do not have permission to view this page.',
    'error.notFound': 'Page not found.',
    'error.reference': 'Reference: {reference}',
    'error.retry': 'Try again',
    'error.technical': 'Technical details (for developers and testers)',
    'state.loading': 'Loading…',
    'state.empty': 'Nothing to show.',
    'state.offline': 'You are offline.',

    'identity.nameAr': 'Arabic name',
    'identity.nameEn': 'Latin-script name',
    'identity.address': 'Address',
    'identity.contactPhone': 'Nursery phone',
    'identity.contactEmail': 'Nursery email',
    'identity.brandColor': 'Nursery colour',
    'identity.brandColor.hint': 'Optional. Write it as #1A7F64, or pick it from the box.',
    'identity.brandColor.pick': 'Pick the colour',
    'identity.weekendDays': 'Weekly days off',
    'identity.weekendDays.hint': 'A child is not counted absent on these days. Leave at least one day open.',
    'identity.weekend': 'Weekly days off: {days}',
    'weekday.Sunday': 'Sunday',
    'weekday.Monday': 'Monday',
    'weekday.Tuesday': 'Tuesday',
    'weekday.Wednesday': 'Wednesday',
    'weekday.Thursday': 'Thursday',
    'weekday.Friday': 'Friday',
    'weekday.Saturday': 'Saturday',

    'action.save': 'Save',
    'action.saving': 'Saving…',
    'action.cancel': 'Cancel',
    'action.close': 'Close',
    'action.confirm': 'Confirm',
    'action.search': 'Search',
    'action.filter': 'Filter',
    'action.add': 'Add',
    'action.edit': 'Edit',
    'action.back': 'Back',
    'action.next': 'Next',
    'action.previous': 'Previous',
    'paging.position': '{page} / {total}',
    'action.copy': 'Copy',
    'action.copied': 'Copied',
    'action.print': 'Print',
    'action.printing': 'Preparing…',

    'pref.language': 'Language',
    'pref.theme': 'Appearance',
    'pref.theme.light': 'Light',
    'pref.theme.dark': 'Dark',
    'pref.theme.system': 'System',
    'pref.numerals': 'Numerals',
    'pref.numerals.western': '123',
    'pref.numerals.eastern': '١٢٣',

    'child.one': 'child',
    'children.count.one': '1 child',
    'children.count.other': '{count} children',
    'age.years.one': '1 year',
    'age.years.other': '{count} years',
    'age.months.zero': 'under a month',
    'age.months.one': '1 month',
    'age.months.other': '{count} months',
    'age.both': '{years} {months}',
    'class.one': 'class',
    'classes.count.one': '1 class',
    'classes.count.other': '{count} classes',
    'staff.one': 'staff member',
    'role.owner': 'Owner',
    'role.manager': 'Manager',
    'role.accountant': 'Accountant',
    'role.teacher': 'Teacher',
    'role.assistant': 'Assistant',
    'role.reception': 'Reception',
    'role.nurse': 'Nurse',
    'role.guardian': 'Guardian',
    'role.superadmin': 'Superadmin',
    'role.support_employee': 'Support employee',
    'role.billing_employee': 'Billing employee',
    'gender.Male': 'Boy',
    'gender.Female': 'Girl',

    'allergy.warning.title': 'Allergy — do not serve:',
    'allergy.severity.Mild': 'mild allergy',
    'allergy.severity.Severe': 'severe allergy',
    'allergy.severity.Anaphylactic': 'life-threatening allergy',
    'allergy.mark': '{allergen} — {severity}',
    'allergy.marks': 'Allergies: {list}',
    'allergy.picker.empty': 'No allergen written yet',
    'allergy.picker.suggestions': 'Matching allergens',
    'allergy.picker.unknown': 'This allergen is not in the platform’s list yet, so it shows a generic picture until it is added. Write the allergen alone, such as "sesame".',

    'presence.present': 'At nursery now',
    'presence.departed': 'Left at {time}',
    'presence.notArrived': 'Not checked in yet',
    'presence.absenceReported': 'Absence reported',

    'status.Trial': 'Trial',
    'status.Active': 'Active',
    'status.PastDue': 'Past due',
    'status.Suspended': 'Suspended',
    'status.Archived': 'Archived',
    'status.Enrolled': 'Enrolled',
    'status.Waitlisted': 'Waitlisted',
    'status.Withdrawn': 'Withdrawn',
    'status.Graduated': 'Graduated',

    'support.banner': 'You are working inside another nursery’s data under an approved support session. Every action is logged.',

    'pending.title': 'This screen is still being built',
    'pending.body': 'The API for this feature ({requirement}) has not shipped yet. Data will appear here as soon as it does.',
    'fixtures.banner': 'Sample data, for demonstration only — not real records.',

    'status.Pending': 'Pending',
    'status.Draft': 'Draft',
    'status.Published': 'Published',
    'status.Issued': 'Issued',
    'status.PartiallyPaid': 'Partly paid',
    'status.Paid': 'Paid',
    'status.Overdue': 'Overdue',
    'status.Void': 'Void',
    'status.Invited': 'Invited',
    'status.Deactivated': 'Deactivated',

    'day.title': 'My day',
    'day.today': 'Today',
    'day.yesterday': 'Yesterday',
    'day.previous': 'Previous day',
    'day.next': 'Next day',
    'day.empty': 'Nothing recorded for this day yet. It will appear here as it is logged.',
    'day.draft': 'This day has not been published to guardians yet.',
    'day.publish': 'Publish this day',
    'day.published': 'The day has been published.',
    'day.recordedBy': 'Recorded by {name}',
    'day.section.attendance': '🕗 Attendance',
    'day.section.meals': '🍳 Meals',
    'day.section.drinks': '💧 Drinks',
    'day.section.activities': '🎨 Activities',
    'day.section.sleep': '😴 Sleep',
    'day.section.mood': '😊 Mood',
    'day.section.diaper': '🧷 Nappy / toilet',
    'day.section.notes': '📝 Note from the teacher',
    'day.arrived': 'Arrived {time}',
    'day.left': 'Left {time}',
    'day.sleeping': 'Asleep since {time}',
    'day.napRange': '{from} to {to}',
    'day.creamApplied': 'Cream applied',
    'day.photoCount': 'Photos from this day: {count}',

    'care.kind.Meal': 'Meal',
    'care.kind.Drink': 'Drink',
    'care.kind.Sleep': 'Sleep',
    'care.kind.Activity': 'Activity',
    'care.kind.Mood': 'Mood',
    'care.kind.Diaper': 'Nappy',
    'care.kind.Note': 'Note',
    'care.amount.All': 'Ate everything',
    'care.amount.Most': 'Ate most',
    'care.amount.Some': 'Ate half',
    'care.amount.Little': 'Ate a little',
    'care.amount.Refused': 'Refused',
    'care.mealType': 'Meal',
    'care.foodItems': 'Food',
    'care.amountEaten': 'Amount eaten',
    'care.note': 'Note',
    'care.saved': 'Saved.',
    'care.allergyAcknowledge': 'I have read the allergy warning above',
    'care.wholeClass': 'Apply to the whole class',
    'care.selectedChildren': '{count} selected',
    'care.time': 'Time',
    'care.time.future': 'A time that has not come yet cannot be recorded. Choose one that has passed.',

    'careDay.title': 'Recorded today',
    'careDay.titleOn': 'Recorded on {date}',
    'careDay.empty': 'Nothing has been recorded for this day yet.',
    'careDay.draft': 'Draft',
    'careDay.published': 'Published',
    'careDay.waitsForReview': 'This day waits for review before families see it. The lead teacher or the administration publishes it.',
    'careDay.wokeUp': 'End the nap now',
    'careDay.delete': 'Delete',
    'careDay.confirmDelete': 'Confirm delete',
    'careDay.editTitle': 'Edit: {kind}',
    'careDay.sleptAt': 'Fell asleep',
    'careDay.wokeAt': 'Woke up',

    'attendance.title': 'Attendance',
    'attendance.board': 'Attendance board',
    'attendance.checkIn': 'Check in',
    'arrival.title': 'Health check on arrival',
    'arrival.temperature': 'Temperature (°C)',
    'arrival.temperature.hint': 'Like 37.2. Leave it empty if it was not taken.',
    'arrival.temperature.invalid': 'Write the temperature as a number with one decimal at most, like 37.2.',
    'arrival.note': 'Anything noticed on arrival',
    'arrival.note.hint': 'A cough, a rash, a scratch… Leave it empty if nothing was noticed.',
    'arrival.reading': 'Temperature {value}°',
    'attendance.checkOut': 'Check out',
    'attendance.present': 'Present',
    'attendance.absent': 'Absent',
    'attendance.notified': 'Absence notified',
    'attendance.expected': 'Not arrived yet',
    'attendance.departed': 'Departed',
    'attendance.closedDay': 'The nursery is closed today, so nobody is counted absent.',
    'attendance.correct': 'Correct',
    'attendance.correctOf': 'Correct the times for {name}',
    'attendance.correctReason': 'Why it is corrected',
    'attendance.checkInOf': 'Check {name} in',
    'attendance.checkOutOf': 'Check {name} out',
    'attendance.history': 'Attendance history',
    'attendance.previousMonth': 'Previous month',
    'attendance.nextMonth': 'Next month',
    'attendance.operatingDays': 'Days open: {count}',
    'attendance.notices': 'Absence notices',
    'attendance.noNotices': 'No absence notice for these days.',
    'attendance.noticeSent': 'Sent',
    'attendance.beforeCutoff': 'It is still before the absence cut-off.',
    'attendance.collectedBy': 'Collected by {name}',
    'attendance.pickupOverride': 'Unauthorised collection',
    'attendance.choosePickup': 'Who is collecting?',
    'attendance.pickupOther': 'Someone else (a reason is required)',
    'attendance.doNotRelease': 'Do not release the child to',
    'attendance.doNotRelease.suspended': 'A suspended guardian. Contact the administration if they come.',
    'attendance.doNotRelease.acknowledge': 'I have read the list of people the child is not released to, and the collector is not one of them.',
    'attendance.guardians': 'Guardians',
    'attendance.pickups': 'Authorised to collect',
    'attendance.pickupName': 'Name of the person collecting',
    'attendance.pickupReason': 'Reason for the exception',
    'attendance.temperature': 'Temperature (°C)',
    'attendance.symptoms': 'Visible symptoms',
    'attendance.notifyAbsence': 'Report an absence',
    'attendance.absenceFrom': 'From',
    'attendance.absenceTo': 'To',
    'attendance.absenceReason': 'Reason (optional)',
    'attendance.absenceSent': 'The nursery has been told.',
    'attendance.summary': '{present} present · {absent} absent · {notified} notified',

    'photos.title': 'Photos',
    'photos.empty': 'No photos yet.',
    'photos.count.one': '1 photo',
    'photos.count.other': '{count} photos',
    'photos.occasion': 'Occasion',
    'photos.allOccasions': 'All occasions',
    'photos.upload': 'Upload a photo or video',
    'photos.video': 'Video',
    'photos.video.hint': 'Videos up to {mb} MB. On an iPhone, choose “Most Compatible” in Settings → Camera → Formats, so the video plays on every device.',
    'photos.tooLarge.photo': 'The photo is larger than {mb} MB. Choose a smaller one.',
    'photos.tooLarge.video': 'The video is larger than {mb} MB. Trim it, or record it at a lower quality.',
    'photos.visibility': 'Who can see it',
    'photos.visibility.SingleChild': 'This child’s guardians only',
    'photos.visibility.SharedTagged': 'The guardians of every tagged child',
    'photos.caption': 'Caption',
    'photos.tagged': 'Children in the photo',
    'photos.uploaded': 'Photo uploaded.',

    'consent.title': 'Consents',
    'consent.hint': 'Recorded by the administration from the signed consent form. Without individual-photos consent the child is not photographed or tagged in any photo.',
    'consent.IndividualPhotos': 'Individual photos',
    'consent.GroupPhotos': 'Group photos',
    'consent.Outings': 'Outings',
    'consent.EmergencyMedicalTreatment': 'Emergency medical treatment',
    'consent.granted': 'Granted',
    'consent.notGranted': 'Not granted',
    'consent.unanswered': 'Not recorded',
    'consent.grant': 'Record consent',
    'consent.refuse': 'Record refusal',
    'consent.revoke': 'Withdraw',
    'consent.evidence': 'Signed form reference',
    'consent.evidence.hint': 'The form number or where it is filed. Required to record a consent.',
    'consent.evidence.required': 'Enter the signed form reference before recording a consent.',
    'consent.onBehalfOf': 'On behalf of',
    'consent.onBehalfOf.none': 'The form names no particular guardian',
    'consent.onBehalfOfName': 'On behalf of {name}',
    'consent.recordedBy': 'Recorded by {name} on {date}',
    'consent.withdrawnPhotos.zero': 'No photo was withdrawn.',
    'consent.withdrawnPhotos.one': '1 photo was withdrawn from every gallery and conversation.',
    'consent.withdrawnPhotos.other': '{count} photos were withdrawn from every gallery and conversation.',
    'consent.registration': 'Family consents',
    'consent.registration.hint': 'As the family signed them on the registration form. Anything left unanswered appears in the Missing consents list.',
    'consentGaps.title': 'Missing consents',
    'consentGaps.lede': 'Children with a consent nobody has answered yet. No child is photographed before their individual-photos consent is recorded. Open the child\'s page and record what their signed form says.',
    'consentGaps.column.unanswered': 'Unanswered',
    'consentGaps.photographable': 'Can be photographed',
    'consentGaps.notPhotographable': 'Not photographed',
    'consentGaps.empty': 'Every consent is recorded for every child.',
    'consentGaps.notice': 'Children with a consent still unanswered: {count}.',
    'consentGaps.open': 'Show the list',
    'photos.noConsent': 'No consent to be photographed',
    'photos.noGroupConsent': 'No group-photos consent',
    'photos.childNotPhotographable': 'The administration has not recorded this child\'s family consent to individual photos, so no photo of them can be uploaded. Ask the administration to record it from the signed form.',
    'photos.taggedNames': 'In the photo: {names}',
    'photos.status.Pending': 'Not published',
    'photos.status.Published': 'Published',
    'photos.status.Withdrawn': 'Withdrawn',
    'photos.correct': 'Correct',
    'photos.correctPublished': 'This photo is published. The change reaches families at once: a child you remove is no longer shown it, and a child you add has their family told.',
    'photos.untaggable': '{name} (no consent)',
    'photos.republish': 'Publish again',
    'photos.remove': 'Delete',
    'photos.removeWarning': 'The photo is deleted from every family\'s gallery and cannot be restored.',
    'photos.confirmRemove': 'Yes, delete it',

    'messages.title': 'Messages',
    'messages.empty': 'No conversations yet.',
    'messages.new': 'New conversation',
    'messages.to': 'To',
    'messages.write': 'Write a message…',
    'messages.send': 'Send',
    'messages.read': 'Read',
    'messages.delivered': 'Delivered',
    'messages.about': 'About {name}',
    'messages.attachment': 'Attachment',
    'notifications.bell': 'Notifications',
    'notifications.bell.unread': 'Notifications, unread: {count}',
    'notifications.seeAll': 'See all notifications',
    'messages.unavailable': 'This photo or video is no longer available.',
    'messages.photo.open': 'Open the photo full size',
    'watermark.text': '{name} · {phone} · {time}',
    'media.fullscreen': 'Full screen',
    'media.fullscreen.exit': 'Exit full screen',
    'date.placeholder': 'D/M/YYYY',
    'date.pick': 'Pick the date from the calendar',
    'date.invalid': 'Write the date day first, then month, then year, like 13/5/2025, or pick it from the calendar.',
    'date.tooEarly': 'Choose a date on or after {date}.',
    'date.tooLate': 'Choose a date up to {date}.',
    'messages.image.send': 'Send a picture',
    'messages.image.hint': 'A prescription, or something you want the nursery to see. Whatever you typed in the message box goes with it.',
    'messages.image.unreadable': 'This file could not be opened as a picture. Choose a JPEG or PNG, or take the picture with the camera.',
    'messages.image.tooLarge': 'The picture is too large even after shrinking it. The limit is {mb} MB. Choose a smaller one.',
    'messages.delete': 'Delete',
    'messages.delete.confirm': 'Confirm delete',
    'messages.voice.hold': 'Hold to record',
    'messages.voice.release': 'Release to send · {time}',
    'messages.voice.hint': 'The first time, your browser asks to use the microphone. Allow it, then hold the button and speak.',
    'messages.voice.ready': 'The microphone is allowed. Now hold the button, speak, and release to send.',
    'messages.voice.denied': 'The microphone is not allowed. Allow it for this site in your browser settings, then try again.',
    'messages.voice.noMicrophone': 'This device has no microphone. Connect one and try again.',
    'messages.voice.unsupported': 'This browser cannot record sound. Update it, or use Chrome or Safari.',
    'messages.voice.failed': 'Recording could not start. Close other apps that use the microphone and try again.',
    'messages.voice.tooShort': 'That was too short. Keep holding while you speak.',
    'messages.voice.limit': 'Recording stopped at {duration}, the longest a voice note can be, and it was sent.',
    'messages.voice.label': 'Voice note, {time} long',
    'messages.voice.unplayable': 'This browser cannot play this voice note ({time}). Open the conversation in another browser to hear it.',
    'messages.share': 'Share from the gallery',
    'messages.share.empty': 'This child has no published photos or videos that can be shared here.',
    'messages.share.send': 'Send selected ({count} of {max})',
    'messages.share.limit': 'You can choose at most {max} items for one message.',

    'announcements.title': 'Announcements',
    'announcements.new': 'New announcement',
    'announcements.subject': 'Title',
    'announcements.body': 'Body',
    'announcements.audience': 'Audience',
    'announcements.audience.AllGuardians': 'All guardians',
    'announcements.audience.ClassGuardians': 'One class’s guardians',
    'announcements.audience.SpecificGuardian': 'One guardian',
    'announcements.audience.AllStaff': 'All staff',
    'announcements.schedule': 'Schedule delivery',
    'announcements.scheduleTime': 'Delivery time',
    'announcements.sendNow': 'Send now',
    'announcements.delivery': 'Reached {delivered} of {total} · {read} read',
    'announcements.empty': 'No announcements.',
    'calendar.title': 'Calendar',
    'calendar.new': 'New entry',
    'calendar.type.Closure': 'Closure',
    'calendar.type.Event': 'Event',
    'calendar.type.Holiday': 'Holiday',
    'calendar.type.Trip': 'Trip',
    'calendar.affectsAttendance': 'Excluded from absence calculation',
    'calendar.empty': 'Nothing coming up.',
    'calendar.remove': 'Remove',
    'calendar.removeOf': 'Remove {title}',
    'calendar.confirmRemove': 'Confirm removing',
    'calendar.editOf': 'Edit {title}',
    'calendar.removeWarning': 'These days will count towards absence again.',
    'calendar.changeWarning': 'Changing a closure or holiday changes which days count towards absence, on the boards and the dashboard.',
    'calendar.hint': 'Closures, holidays, events and trips coming up, as the administration entered them.',
    'calendar.range': '{from} – {to}',
    'calendar.forClass': 'For {name}',
    'calendar.closed': 'The nursery is closed',

    'payments.title': 'Fees',
    'payments.currentFee': 'This period’s fee',
    'payments.paid': 'Paid',
    'payments.remaining': 'Remaining',
    'payments.credit': 'Credit in your favour',
    'payments.lastPayment': 'Last payment: {amount} on {date}',
    'payments.noPayments': 'No payments recorded.',
    'payments.history': 'Payment history',
    'payments.plan': 'Fee plan',
    'payments.method': 'Method',
    'payments.method.Cash': 'Cash',
    'payments.method.BankTransfer': 'Bank transfer',
    'payments.method.Cheque': 'Cheque',
    'payments.method.Other': 'Other',
    'payments.record': 'Record a payment',
    'payments.amount': 'Amount',
    'payments.date': 'Date',
    'payments.reference': 'Reference',
    'payments.paidBy': 'Paid by',
    'payments.paidBy.none': 'Not recorded',
    'payments.yearTotal': 'Fees for {year}',
    'payments.deadline': 'Pay by {date}',
    'payments.recorded': 'Payment recorded.',
    'payments.void': 'Void payment',
    'payments.voidReason': 'Reason',
    'invoices.title': 'Invoices',
    'invoices.number': 'Number',
    'invoices.period': 'Period',
    'invoices.dueDate': 'Due',
    'invoices.total': 'Total',
    'invoices.outstanding': 'Outstanding',
    'invoices.generate': 'Generate the period’s invoices',
    'invoices.generated': '{created} invoices created, {skipped} skipped.',
    'invoices.issue': 'Issue',
    'invoices.void': 'Void',
    'finance.void.reason': 'Reason for voiding',
    'finance.void.confirm': 'Confirm void',
    'finance.void.invoiceOf': 'Void invoice {number}',
    'finance.void.paymentOf': 'Void the payment for {name}',
    'finance.void.invoiceHint': 'The invoice stays on record, struck through, and its payments become credit for the family.',
    'finance.void.paymentHint': 'The payment stays on record, struck through, and what it paid is owed again.',
    'finance.voided': 'Voided: {reason}',
    'invoices.daysOverdue': '{days} days overdue',
    'invoices.empty': 'No invoices.',
    'finance.overdue': 'Overdue',
    'finance.overdueTotal': 'Total overdue: {amount}',
    'finance.income': 'Income report',
    'finance.incomeTotal': 'Received',
    'finance.previousPeriod': 'Previous period',
    'finance.byClass': 'By class',
    'finance.byMethod': 'By method',
    'finance.sendReminders': 'Send reminders',
    'finance.remindersSent': '{sent} reminders sent, {skipped} skipped.',
    'finance.from': 'From',
    'finance.to': 'To',

    'health.title': 'Health and safety',
    'health.record': 'Health record',
    'health.bloodType': 'Blood type',
    'health.chronic': 'Chronic conditions',
    'health.dietary': 'Dietary restrictions',
    'health.doctor': 'Doctor',
    'health.doctorPhone': 'Doctor’s phone',
    'health.vaccinations': 'Vaccinations',
    'health.napHabits': 'Nap habits',
    'health.comfortItem': 'Comfort item',
    'health.toiletStage': 'Toilet training stage',
    'health.regularMedication': 'Regular medication',
    'health.notes': 'Other notes',
    'health.hint': 'What the family told the nursery. Kept by whoever looks after health.',
    'health.empty': 'Nothing recorded yet.',
    'incidents.title': 'Incidents',
    'incidents.printOf': 'Print the incident record for {name}',
    'incidents.new': 'Record an incident',
    'incidents.type': 'Type',
    'incidents.type.Fall': 'Fall',
    'incidents.type.Bump': 'Bump',
    'incidents.type.Bite': 'Bite',
    'incidents.type.Illness': 'Illness',
    'incidents.type.Behavioural': 'Behavioural',
    'incidents.type.Other': 'Other',
    'incidents.severity': 'Severity',
    'incidents.severity.Minor': 'Minor',
    'incidents.severity.Moderate': 'Moderate',
    'incidents.severity.Serious': 'Serious',
    'incidents.location': 'Where',
    'incidents.description': 'What happened',
    'incidents.bodyArea': 'Body area',
    'incidents.actionTaken': 'Action taken',
    'incidents.firstAid': 'First aid given',
    'incidents.acknowledge': 'I have read this report',
    'incidents.acknowledged': 'Acknowledged {date}',
    'incidents.awaiting': 'Awaiting guardian acknowledgement',
    'incidents.empty': 'No incidents recorded.',
    'incidents.newHint': 'The child’s guardians are told as soon as it is saved, and asked to acknowledge the report.',
    'incidents.date': 'Date',
    'incidents.when': 'When',
    'incidents.firstAidDetail': 'What first aid was given',
    'incidents.firstAidYes': 'Yes',
    'incidents.witness': 'Witness among the staff',
    'incidents.noWitness': 'None',
    'incidents.reportedBy': 'Recorded by',
    'incidents.record': 'Record the incident',
    'incidents.recorded': 'The incident is recorded and the guardians are told.',
    'incidents.of': 'Incident: {name}',
    'incidents.open': 'Open the record',
    'incidents.openToClose': 'Open and close the record',
    'incidents.close': 'Close the record',
    'incidents.confirmClose': 'Confirm closing',
    'incidents.closed': 'Closed {date}',
    'incidents.outcome': 'Outcome',
    'incidents.outcome.ResolvedOnSite': 'Dealt with at the nursery',
    'incidents.outcome.SeenByDoctor': 'Seen by a doctor',
    'incidents.outcome.TakenToHospital': 'Taken to hospital',
    'incidents.outcome.TakenHomeByGuardian': 'Taken home by a guardian',
    'incidents.followUp': 'What happened next',
    'incidents.followUp.hint': 'The guardian reads what you write here.',
    'incidents.prevention': 'What we changed so it does not happen again',
    'incidents.prevention.hint': 'Staff only; the guardian does not see it.',
    'incidents.toldBy': 'How was the guardian told?',
    'incidents.toldBy.hint': 'The guardian has not acknowledged the incident in the app.',
    'incidents.toldBy.PhoneCall': 'By phone',
    'incidents.toldBy.InPerson': 'In person',
    'incidents.unread': 'Not yet read',
    'incidents.history': 'Recorded incidents',
    'medication.title': 'Medication',
    'medication.name': 'Medication',
    'medication.dose': 'Dose',
    'medication.schedule': 'Schedule',
    'medication.maxPerDay': 'Maximum per day',
    'medication.authorize': 'Record a medication',
    'medication.hint': 'Recorded by the administration as the family told them. Guardians enter no medication.',
    'medication.administer': 'Record a dose',
    'medication.dosesToday': 'Given today: {count}',
    'medication.dosesOfMax': 'Given today: {count} of {max}',
    'medication.doseGiven': 'Dose given',
    'medication.confirmGiven': 'Dose given',
    'medication.given': 'The dose is recorded and the guardians are told.',
    'medication.overLimit': 'Today’s doses have reached the recorded daily maximum. Write why another is given.',
    'medication.reason': 'Reason',
    'medication.noneGiven': 'No dose has been given yet.',
    'medication.givenList': 'Doses given',
    'medication.givenBy': 'given by {name}',
    'medication.outOfSchedule': 'outside the schedule: {reason}',
    'medication.dueTitle': 'Medication today',
    'medication.dueEmpty': 'No child in this class has medication in force today.',
    'medication.empty': 'No medication recorded.',
    'medication.form': 'Form',
    'medication.form.choose': 'Choose the form',
    'medication.dose.hint': 'As written: 5, half a tablet, two drops.',
    'medication.doseWithUnit': '{dose} {unit}',
    'medication.doseIn': 'Dose ({unit})',
    'medication.period': '{from} to {to}',
    'medication.since': 'Since {from}',
    'medication.startDate': 'From',
    'medication.endDate': 'Until',
    'medication.notes': 'Notes',
    'medication.informedBy': 'Told by',
    'medication.informedBy.none': 'No one in particular (signed form)',
    'medication.recordedBy': 'Recorded by {name}',
    'medication.informedByName': 'Told by {name}',
    'medication.current': 'Current',
    'medication.ended': 'Ended',
    'medication.required': 'Enter the medication, its form, the dose and when it is given.',

    'staff.title': 'Staff',
    'staff.invite': 'Invite a staff member',
    'staff.name': 'Name',
    'staff.phone': 'Phone',
    'people.numberReleased': 'Number moved to someone else',
    'nav.accountRequests': 'Account requests',
    'accountRequests.title': 'Account recovery requests',
    'accountRequests.lede': 'People who asked to reset their password after a long time without using their account. Confirm each one face to face before approving.',
    'accountRequests.empty': 'No requests are waiting.',
    'accountRequests.requestedAt': 'Asked on',
    'accountRequests.from': 'From',
    'accountRequests.approve': 'Confirmed in person',
    'accountRequests.reject': 'Refuse',
    'accountRequests.approved': 'They can now set a new password with a code sent to their number.',
    'accountRecovery.action': 'Account and number',
    'accountRecovery.title': 'Account and number — {name}',
    'accountRecovery.renumber.title': 'Move the account to a new number',
    'accountRecovery.renumber.hint': 'For someone who lost their number. A link goes to the new number; the account moves when it is opened, and its owner then sets a new password with a code.',
    'accountRecovery.renumber.phone': 'New number',
    'accountRecovery.renumber.submit': 'Send the link',
    'accountRecovery.renumber.sent': 'The link went to {phone}. It expires at {date}.',
    'accountRecovery.release.title': 'Take the number off the account',
    'accountRecovery.release.hint': 'When the number now belongs to someone else. The account keeps its name and records, and nobody can sign in to it from now on.',
    'accountRecovery.release.confirm': 'I confirmed the number is no longer this person\'s',
    'accountRecovery.release.submit': 'Take the number off',
    'accountRecovery.release.done': 'The number was taken off the account.',
    'staff.role': 'Role',
    'staff.jobTitle': 'Job title',
    'staff.classes': 'Classes',
    'staff.permissions': 'Permissions',
    'staff.deactivate': 'End access',
    'staff.deactivated': 'Access ended.',
    'staff.permissionFromRole': 'From role',
    'staff.permissionBlocked': 'Cannot be granted to this role',
    'staff.empty': 'No staff yet.',
    'staff.editOf': 'Edit {name}',
    'staff.details': 'Name, role and job title',
    'staff.role.hint': 'Changing the role changes what they may do from their next request.',
    'staff.saved': 'Saved.',
    'staff.classes.hint': 'A class with dates is cover for a colleague; without dates it is their own class.',
    'staff.classes.none': 'No class yet.',
    'staff.roleInClass': 'Role in the class',
    'staff.roleInClass.Lead': 'Lead teacher',
    'staff.roleInClass.Assistant': 'Assistant',
    'staff.roleInClass.Substitute': 'Cover',
    'staff.assignment.span': 'from {from} to {to}',
    'staff.assign': 'Add the class',
    'staff.assign.from': 'From',
    'staff.assign.to': 'Until',
    'staff.assign.coverHint': 'Leave empty for their own class; set it for cover.',
    'staff.unassign': 'End',
    'staff.unassignFrom': 'End {name}',
    'staff.inviteAgain': 'Invite again',
    'staff.inviteAgainOf': 'Invite {name} again',
    'staff.inviteAgain.hint': 'Accepting this invitation reopens their account with the role chosen here. Their earlier permissions and classes do not come back: give them again after they accept.',

    'guardians.title': 'Guardians',
    'guardians.add': 'Add a guardian',
    'guardians.primary': 'Primary guardian',
    'guardians.relationship': 'Relationship',
    'guardians.relationship.Mother': 'Mother',
    'guardians.relationship.Father': 'Father',
    'guardians.relationship.Grandmother': 'Grandmother',
    'guardians.relationship.Grandfather': 'Grandfather',
    'guardians.relationship.Aunt': 'Aunt',
    'guardians.relationship.Uncle': 'Uncle',
    'guardians.relationship.Sibling': 'Sibling',
    'guardians.relationship.LegalGuardian': 'Legal guardian',
    'guardians.relationship.Other': 'Other',
    'pickups.title': 'Authorised to collect',
    'pickups.add': 'Add someone',
    'pickups.validFrom': 'Valid from',
    'pickups.validTo': 'Valid to',
    'pickups.expired': 'Authorisation expired',
    'pickups.empty': 'Nobody beyond the guardians.',
    'pickups.hint': 'Guardians may always collect. Add anyone else here, once the family has told the nursery.',
    'pickups.name': 'Name',
    'pickups.name.required': 'Enter their name.',
    'pickups.relationship': 'Relation to the child',
    'pickups.idDocument': 'ID number',
    'pickups.remove': 'Withdraw',
    'pickups.validity': 'From {from} to {to}',

    'notifications.title': 'Notifications',
    'notifications.empty': 'No notifications.',
    'notifications.markAllRead': 'Mark all read',
    'notifications.preferences': 'Notification preferences',
    'notifications.mandatory': 'Cannot be switched off',
    'notifications.digested': 'Batched',
    'notifications.quietHours': 'Quiet hours',

    'notifications.type.ChildCheckedIn': 'Your child arrived safely',
    'notifications.type.ChildCheckedOut': 'Your child left the nursery',
    'notifications.type.IncidentRecorded': 'An incident involving a child was recorded',
    'notifications.type.UnauthorizedPickupOverride': 'A child was released to someone not on the authorised list',
    'notifications.type.PickupListChanged': 'The list of who may collect a child changed',
    'notifications.type.DirectMessageReceived': 'A message arrived',
    'notifications.type.AnnouncementPublished': 'An announcement was published',
    'notifications.type.InvoiceIssued': 'An invoice was issued',
    'notifications.type.PaymentDue': 'A payment is due',
    'notifications.type.InvoiceOverdue': 'An invoice is overdue',
    'notifications.type.PaymentRecorded': 'A payment was recorded',
    'notifications.type.MedicationAdministered': 'An authorised medication was given',
    'notifications.type.AbsenceNoticeReceived': 'A guardian gave notice of an absence',
    'notifications.type.AbsenceNoticeWithdrawn': 'A guardian withdrew an absence notice',
    'notifications.type.SupportSessionRequested': "A platform employee asked to see the nursery's data",
    'notifications.type.SupportSessionApproved': 'A support session was approved and is active',
    'notifications.type.SupportSessionWrite': 'A change was made during an active support session',
    'notifications.type.SupportSessionEnded': 'A support session ended',
    'notifications.type.InvitationSent': 'An invitation to join was sent',
    'notifications.type.PlanLimitReached': 'A plan limit was reached',
    'notifications.type.TrialExpiring': 'The trial is about to end',
    'notifications.type.ComplaintSubmitted': 'A guardian raised a complaint',
    'notifications.type.ComplaintAssigned': 'A complaint was assigned to you',
    'notifications.type.ComplaintUpdated': 'Your complaint was acknowledged, answered or resolved',
    'notifications.type.ComplaintReplyReceived': 'A guardian replied to a complaint',
    'notifications.type.AttendanceNotRecorded': 'Children are neither checked in nor reported absent, and the cut-off is near',
    'notifications.type.PhotosPublished': 'New photos were published',
    'notifications.type.DailyCareUpdated': 'New care entries were recorded',
    'notifications.type.EndOfDaySummary': 'The day summary',
    'notifications.type.UpcomingClosureOrEvent': 'A closure or event is coming up',
    'notifications.type.PaymentUpcoming': 'A payment deadline is approaching',

    'dashboard.title': 'Dashboard',
    'metric.children.registered': '👶 Children registered',
    'metric.attendance.present': '🟢 Present today',
    'metric.attendance.absent': '🔴 Absent today',
    'metric.attendance.notified': '🟡 Absence notified',
    'metric.attendance.departed': '👋 Departed',
    'metric.daily.missing': '📔 No entries yet today',
    'metric.messages.unread': '💬 Unread messages',
    'metric.incidents.open': '🚑 Incidents awaiting acknowledgement',
    'metric.staff.onDuty': '🧑‍🏫 Staff on duty',
    'metric.finance.invoiced': '💰 Invoiced this month',
    'metric.finance.collected': '💵 Collected this month',
    'metric.finance.outstanding': '⚠️ Total outstanding',
    'metric.finance.overdue30': '⏰ Overdue by 30+ days',

    'settings.title': 'Settings',
    'settings.saved': 'Settings saved.',
    'settings.reviewOff.warning': 'Saving publishes every day waiting for review to the families, except days in closed terms.',
    'settings.draftsPublished.one': 'One day waiting for review was published.',
    'settings.draftsPublished.other': '{count} days waiting for review were published.',
    'settings.draftsHeld.one': 'One day is still waiting for review because it is in a closed term. Publish it from the child’s page once the term is reopened.',
    'settings.draftsHeld.other': '{count} days are still waiting for review because they are in closed terms. Publish them from the child’s page once the term is reopened.',
    'settings.default': 'Default',
    'settings.range': 'From {min} to {max}',
    'settings.daily.review_required': 'Review the day before publishing it',
    'settings.attendance.cutoff_time': 'Absence cut-off time',
    'settings.attendance.cutoff_alert_minutes': 'Alert before the absence cut-off (minutes, zero to switch off)',
    'settings.attendance.pickup_required': 'Require the collector to be named at check-out',
    'settings.attendance.health_check': 'Health check on arrival',
    'settings.daily.require_check_in': 'Nothing is recorded for a child not checked in',
    'settings.photos.default_visibility': 'Default photo visibility',
    'settings.photos.retention_months': 'Keep photos for (months)',
    'settings.notifications.digest_window_minutes': 'Digest window (minutes)',
    'settings.notifications.sms_enabled': 'Text the important alerts (incidents, unauthorised collections, payments due and overdue). Each text costs money.',
    'settings.messages.attachments_enabled': 'Allow photos, videos and voice notes in conversations',
    'settings.notifications.closing_time': 'Closing time',
    'settings.finance.due_day_of_month': 'Invoice due day',
    'settings.finance.invoice_number_prefix': 'Invoice number prefix',
    'settings.finance.generation_day': 'Automatic invoice day',
    'settings.reminders.send_time': 'Time reminders are sent',
    'settings.reminders.payment_due_schedule': 'Remind about a payment deadline this many days before it (comma separated, such as 30,14,7,3,1; empty for none)',
    'settings.reminders.payment_overdue_every_days': 'After a payment deadline, remind every (days, 0 for never)',
    'settings.reminders.payment_sms_last_steps': 'How many of the last reminders before the deadline also go by SMS',
    'settings.finance.deadline_term': 'The year’s fees fall due at the end of term number',
    'settings.reminders.calendar_event_days': 'Remind about a closure or event this many days before (0 for never)',
    'settings.exports.window_days': 'Days after a child’s enrolment ends that a copy of their records can still be made (0 for current guardians only)',
    'settings.exports.file_days': 'Days the file of a copy is kept for download',
    'settings.retention.daily_care_months': 'Delete a child’s daily reports this long after they leave (months)',
    'settings.retention.attendance_months': 'Delete a child’s attendance this long after they leave (months)',
    'settings.retention.finance_months': 'Delete a child’s invoices and payments this long after they leave (months)',

    'audit.title': 'Audit log',
    'audit.actor': 'User',
    'audit.entity': 'Record',
    'audit.action': 'Action',
    'audit.when': 'When',
    'audit.empty': 'Nothing matches.',

    'import.title': 'Import children',
    'import.hint': 'Upload a CSV with the columns: first name, last name, date of birth, gender, class, enrolment date, guardian name, guardian phone, emergency contact name, their phone, their relationship, allergies ("none" or the allergens separated by commas). The emergency contact and the allergies are required.',
    'import.choose': 'Choose a file',
    'import.validate': 'Check the file',
    'import.commit': 'Commit the import',
    'import.summary': '{valid} valid of {total} · {invalid} invalid',
    'import.blocked': 'Fix the errors first — a file is never imported in part.',
    'import.done': '{count} children imported.',

    'complaint.reference': 'Reference',
    'complaint.category': 'About',
    'complaint.category.StaffConduct': 'A member of staff',
    'complaint.category.CareQuality': 'Quality of care',
    'complaint.category.Safety': 'Safety',
    'complaint.category.Food': 'Food',
    'complaint.category.Facilities': 'Premises and cleanliness',
    'complaint.category.Billing': 'Fees and invoices',
    'complaint.category.Communication': 'Being kept informed',
    'complaint.category.Other': 'Something else',

    'complaint.status': 'Status',
    'complaint.status.Submitted': 'Received',
    'complaint.status.Acknowledged': 'Being looked at',
    'complaint.status.Assigned': 'With someone',
    'complaint.status.InProgress': 'Being dealt with',
    'complaint.status.Resolved': 'Dealt with',
    'complaint.status.Closed': 'Closed',
    'complaint.status.Withdrawn': 'Withdrawn',

    'complaint.severity': 'Priority',
    'complaint.severity.Low': 'Low',
    'complaint.severity.Normal': 'Normal',
    'complaint.severity.High': 'Urgent',

    'complaint.event.Submitted': 'Complaint raised',
    'complaint.event.Acknowledged': 'The setting confirmed it arrived',
    'complaint.event.Assigned': 'Handed to {name}',
    'complaint.event.Reassigned': 'Moved to {name}',
    'complaint.event.InternalNote': 'Internal note',
    'complaint.event.ReplyToGuardian': 'Reply from the setting',
    'complaint.event.GuardianReply': 'Reply from the guardian',
    'complaint.event.SeverityChanged': 'Priority changed',
    'complaint.event.StatusChanged': 'Status changed',
    'complaint.event.Resolved': 'What was done',
    'complaint.event.Reopened': 'Reopened',
    'complaint.event.Closed': 'Closed',
    'complaint.event.Withdrawn': 'The guardian withdrew it',

    ...accountStrings.en,
  },
};
